import React, { useState, useEffect, useContext } from 'react';
import { browserHistory } from 'react-router';

import { Form, Typography, Button, message } from 'antd';
import {
  AntdPageHeader,
  ButtonGroup,
  DragAndDropSubcontent,
  ApplicationUIContext,
  Center,
} from '@yuhuinc/ui-library';
import { LoadingOutlined } from '@ant-design/icons';

import TemplateForm from './Templates/TemplateForm';
import { Draft, Published, Archived } from './store/constants';

import {
  translationsToObject,
  getTemplate,
  updateTemplate,
  archiveTemplate,
  deleteTemplate,
  restoreTemplate,
} from './store/actions';
import { getTranslatedString } from 'helpers/i18n';

const { Title } = Typography;

const saveSuccessText = getTranslatedString(
  'landlordPortal.applicationTemplate.messages.saveTemplate.success'
);
const saveErrorText = getTranslatedString(
  'landlordPortal.applicationTemplate.messages.saveTemplate.error'
);
const publishSuccessText = getTranslatedString(
  'landlordPortal.applicationTemplate.messages.publishTemplate.success'
);
const publishErrorText = getTranslatedString(
  'landlordPortal.applicationTemplate.messages.publishTemplate.error'
);
const archiveSuccessText = getTranslatedString(
  'landlordPortal.applicationTemplate.messages.archiveTemplate.success'
);
const archiveErrorText = getTranslatedString(
  'landlordPortal.applicationTemplate.messages.archiveTemplate.error'
);
const deleteSuccessText = getTranslatedString(
  'landlordPortal.applicationTemplate.messages.deleteTemplate.success'
);
const deleteErrorText = getTranslatedString(
  'landlordPortal.applicationTemplate.messages.deleteTemplate.error'
);
const restoreSuccessText = getTranslatedString(
  'landlordPortal.applicationTemplate.messages.restoreTemplate.success'
);
const restoreErrorText = getTranslatedString(
  'landlordPortal.applicationTemplate.messages.restoreTemplate.error'
);

const EditApplicationTemplate = ({ routeParams, location }) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const [applicationTemplateForm] = Form.useForm();
  const [applicationTemplate, setApplicationTemplate] = useState(null);

  useEffect(() => {
    getTemplate(routeParams.applicationTemplateId).then(({ application_template }) => {
      const formattedTranslations = translationsToObject(application_template.translations);
      setApplicationTemplate({ ...application_template, translations: formattedTranslations });
    });
  }, [routeParams]);

  useEffect(() => {
    if (location.query.flashMessage) {
      message.success(location.query.flashMessage);
    }
  }, []);

  const toApplicationTemplates = (flashMessage) => {
    browserHistory.push(
      `/companies/application_templates${
        flashMessage && typeof flashMessage === 'string'
          ? `?flashMessage=${encodeURIComponent(flashMessage)}`
          : ''
      }`
    );
  };

  const toApplicationTemplateStep = () => {
    const step = applicationTemplateForm.getFieldValue(['steps']).length + 1;
    browserHistory.push(
      `/companies/application_templates/${applicationTemplate.id}/steps/new?step=${step}`
    );
  };

  const handleSave = () => {
    applicationTemplateForm.validateFields().then((values) => {
      updateTemplate(routeParams.applicationTemplateId, values)
        .done(() => toApplicationTemplates(saveSuccessText))
        .catch((_) => message.error(saveErrorText));
    });
  };

  const handlePublish = () => {
    applicationTemplateForm.validateFields().then((values) => {
      updateTemplate(routeParams.applicationTemplateId, { ...values, status: Published })
        .done(() => toApplicationTemplates(publishSuccessText))
        .catch((_) => message.error(publishErrorText));
    });
  };

  const handleArchive = () => {
    archiveTemplate(routeParams.applicationTemplateId)
      .done(() => toApplicationTemplates(archiveSuccessText))
      .catch((_) => message.error(archiveErrorText));
  };

  const handleDelete = () => {
    deleteTemplate(routeParams.applicationTemplateId)
      .done(() => toApplicationTemplates(deleteSuccessText))
      .catch((_) => message.error(deleteErrorText));
  };

  const handleRestore = () => {
    restoreTemplate(routeParams.applicationTemplateId)
      .done(() => toApplicationTemplates(restoreSuccessText))
      .catch((error) => message.error(restoreErrorText));
  };

  const handleStepEdit = ({ stepId, stepIndex }) => {
    browserHistory.push(
      `/companies/application_templates/${routeParams.applicationTemplateId}/steps/${stepId}/edit?step=${stepIndex}`
    );
  };

  const handlePreview = () => {
    browserHistory.push(
      `/companies/application_templates/${applicationTemplate.id}/wizard/preview`
    );
  };

  const ActionsButtonGroup = ({ status }) => {
    const options = {
      [Draft]: [
        {
          label: getTranslatedString('landlordPortal.applicationTemplate.actions.save'),
          onClick: handleSave,
        },
        {
          label: getTranslatedString('landlordPortal.applicationTemplate.actions.preview'),
          onClick: handlePreview,
        },
        {
          label: getTranslatedString('landlordPortal.applicationTemplate.actions.publish'),
          onClick: handlePublish,
        },
      ],
      [Published]: [
        {
          label: getTranslatedString('landlordPortal.applicationTemplate.actions.archive'),
          onClick: handleArchive,
        },
        {
          label: getTranslatedString('landlordPortal.applicationTemplate.actions.preview'),
          onClick: handlePreview,
        },
        {
          label: getTranslatedString('landlordPortal.applicationTemplate.actions.save'),
          onClick: handleSave,
        },
      ],
      [Archived]: [
        {
          label: getTranslatedString('landlordPortal.applicationTemplate.actions.save'),
          onClick: handleSave,
        },
        {
          label: getTranslatedString('landlordPortal.applicationTemplate.actions.preview'),
          onClick: handlePreview,
        },
        {
          label: getTranslatedString('landlordPortal.applicationTemplate.actions.restore'),
          onClick: handleRestore,
        },
        {
          label: getTranslatedString('landlordPortal.applicationTemplate.actions.delete'),
          onClick: handleDelete,
        },
      ],
    }[status];

    return <ButtonGroup buttons={options} />;
  };

  const onDragFinish = (newList) =>
    applicationTemplateForm.setFieldsValue({ application_template_steps: newList });

  return (
    <div className="body" role="main">
      {!applicationTemplate ? (
        <Center>
          <LoadingOutlined style={{ fontSize: 24 }} spin />
        </Center>
      ) : (
        <>
          <AntdPageHeader
            onBack={toApplicationTemplates}
            title={getTranslatedString('landlordPortal.applicationTemplate.edit')}
            subTitle={applicationTemplate.name}
            extra={<ActionsButtonGroup status={applicationTemplate.status} />}
            status={applicationTemplate.status}
          />

          <TemplateForm
            form={applicationTemplateForm}
            formName={'edit_application_template'}
            formData={applicationTemplate}
          >
            <Title level={5}>
              {getTranslatedString('landlordPortal.applicationTemplate.steps')}
            </Title>

            <Form.List name="steps">
              {(fields, operations) => {
                const steps = fields.map((field) => ({
                  key: field.key,
                  id: applicationTemplateForm.getFieldValue(['steps', field.name, 'id']),
                  label: applicationTemplateForm.getFieldValue(['steps', field.name, 'label']),
                  onDelete: () => operations.remove(field.name),
                  onEdit: () => {
                    handleStepEdit({
                      stepId: applicationTemplateForm.getFieldValue(['steps'])[field.name].id,
                      stepIndex: field.name + 1,
                    });
                  },
                }));

                return (
                  <DragAndDropSubcontent
                    contentList={steps}
                    onDragFinish={onDragFinish}
                    isMobile={isMobile}
                  />
                );
              }}
            </Form.List>

            <Button size={isMobile && 'large'} onClick={toApplicationTemplateStep}>
              {getTranslatedString('landlordPortal.applicationTemplate.steps.add')}
            </Button>
          </TemplateForm>
        </>
      )}
    </div>
  );
};

export default EditApplicationTemplate;
